/**
 * HubAddressesPage — V14.68.5 (Feb 2026)
 *
 * "Mes lieux" style HUB — carte + arbre catégories + panneau détail droit.
 * Route: /hub/addresses (non-destructive : HubDashboard actuel préservé).
 *
 * Layout 3-col :
 *   [ Sidebar catégories ] [ Map centrale ]  [ Panneau détail droit ]
 *
 * Data: GET /api/hub/addresses → tous les entries de l'utilisateur + son org.
 * Actions droite : link-project, share (copy link), edit label, delete.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useSuppressMapBackground } from '../contexts/MapBackgroundContext';
import {
  MapPin, Home, Briefcase, Share2, Copy, Edit3, Trash2, Plus, Search, X, ChevronRight, ChevronDown, Loader2, ExternalLink, Layers,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAddressSuggest } from '../hooks/useAddressSuggest';
import DepartementHeatmap from '../components/hub/DepartementHeatmap';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Custom pin builder — colored by category (pro = ambre, perso = bleu, w/ projects = vert)
function makePin(category, hasProjects, selected) {
  const color = hasProjects ? '#10B981' : (category === 'pro' ? '#F59E0B' : '#3B82F6');
  const scale = selected ? 1.35 : 1;
  return L.divIcon({
    className: 'hub-addr-pin',
    html: `<div style="transform:scale(${scale});transform-origin:bottom center;transition:transform .15s ease;">
      <svg width="26" height="34" viewBox="0 0 26 34" xmlns="http://www.w3.org/2000/svg">
        <path d="M13 0C5.82 0 0 5.82 0 13c0 9.75 13 21 13 21s13-11.25 13-21C26 5.82 20.18 0 13 0z" fill="${color}" stroke="${selected ? '#fff' : 'rgba(0,0,0,0.25)'}" stroke-width="${selected ? 2 : 1}"/>
        <circle cx="13" cy="13" r="5" fill="white"/>
      </svg>
    </div>`,
    iconSize: [26, 34],
    iconAnchor: [13, 34],
    popupAnchor: [0, -30],
  });
}

// Fly to selected address on selection
function FlyTo({ coords }) {
  const map = useMap();
  useEffect(() => {
    if (coords && coords.length === 2) {
      map.flyTo([coords[0], coords[1]], Math.max(map.getZoom(), 15), { duration: 0.6 });
    }
  }, [coords, map]);
  return null;
}

// Group addresses by category / subcategory for the left tree
function useCategoryTree(items) {
  return useMemo(() => {
    const tree = { pro: {}, perso: {} };
    for (const a of items) {
      const c = a.category === 'perso' ? 'perso' : 'pro';
      const sc = (a.subcategory || 'autres').trim() || 'autres';
      if (!tree[c][sc]) tree[c][sc] = [];
      tree[c][sc].push(a);
    }
    return tree;
  }, [items]);
}

export default function HubAddressesPage() {
  const navigate = useNavigate();
  // Cette page a sa propre MapContainer plein écran → on masque le fond
  // map global pendant sa durée de vie pour éviter le double render.
  useSuppressMapBackground();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [expandedCat, setExpandedCat] = useState({ pro: true, perso: true });
  const [expandedSub, setExpandedSub] = useState({});
  const [searchQ, setSearchQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [tileType, setTileType] = useState('satellite'); // 'satellite' | 'street'
  const [geoFilter, setGeoFilter] = useState(null); // { key: 'commune'|'departement'|'pays', value: string }
  const [heatmap, setHeatmap] = useState(false); // V14.68.9 — département choropleth

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/hub/addresses`, { withCredentials: true });
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error('Impossible de charger tes adresses');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const selected = useMemo(() => items.find((i) => i.address_id === selectedId) || null, [items, selectedId]);

  // Phase C — geo facets (commune / departement / pays) computed from geo_meta
  const geoFacets = useMemo(() => {
    const c = new Map(), d = new Map(), p = new Map();
    for (const a of items) {
      const m = a.geo_meta || {};
      if (m.commune) c.set(m.commune, (c.get(m.commune) || 0) + 1);
      if (m.departement) d.set(m.departement, (d.get(m.departement) || 0) + 1);
      if (m.pays) p.set(m.pays, (p.get(m.pays) || 0) + 1);
    }
    const sort = (m) => Array.from(m.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
    return { commune: sort(c), departement: sort(d), pays: sort(p) };
  }, [items]);

  const filteredItems = useMemo(() => {
    let list = items;
    if (geoFilter) {
      list = list.filter((a) => (a.geo_meta || {})[geoFilter.key] === geoFilter.value);
    }
    const q = (searchQ || '').trim().toLowerCase();
    if (!q) return list;
    return list.filter((a) =>
      (a.label || '').toLowerCase().includes(q)
      || (a.address || '').toLowerCase().includes(q)
      || (a.subcategory || '').toLowerCase().includes(q),
    );
  }, [items, searchQ, geoFilter]);

  const tree = useCategoryTree(filteredItems);

  // Map default center: average of all coords or Paris fallback
  const mapCenter = useMemo(() => {
    if (items.length === 0) return [46.6, 2.5]; // France
    const s = items.reduce((acc, a) => {
      if (Array.isArray(a.coords) && a.coords.length === 2) {
        acc.lat += a.coords[0]; acc.lon += a.coords[1]; acc.n += 1;
      }
      return acc;
    }, { lat: 0, lon: 0, n: 0 });
    return s.n > 0 ? [s.lat / s.n, s.lon / s.n] : [46.6, 2.5];
  }, [items]);

  // Actions
  const doPatch = async (aid, patch) => {
    try {
      const r = await axios.patch(`${API}/hub/addresses/${aid}`, patch, { withCredentials: true });
      setItems((cur) => cur.map((x) => (x.address_id === aid ? r.data : x)));
      toast.success('Enregistré');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const doDelete = async (aid) => {
    if (!window.confirm('Supprimer cette adresse ?')) return;
    try {
      await axios.delete(`${API}/hub/addresses/${aid}`, { withCredentials: true });
      setItems((cur) => cur.filter((x) => x.address_id !== aid));
      if (selectedId === aid) setSelectedId(null);
      toast.success('Supprimée');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const doShare = async (aid) => {
    try {
      const r = await axios.post(`${API}/hub/addresses/${aid}/share`, {}, { withCredentials: true });
      const url = `${window.location.origin}/hub/shared/${r.data.share_token}`;
      await navigator.clipboard.writeText(url);
      setItems((cur) => cur.map((x) => (x.address_id === aid ? { ...x, share_token: r.data.share_token } : x)));
      toast.success('Lien copié dans le presse-papier');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const doRevokeShare = async (aid) => {
    try {
      await axios.delete(`${API}/hub/addresses/${aid}/share`, { withCredentials: true });
      setItems((cur) => cur.map((x) => (x.address_id === aid ? { ...x, share_token: null } : x)));
      toast.success('Partage révoqué');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };
  const doCreateFromSuggestion = async (s, cat = 'pro', subcat = null) => {
    try {
      const r = await axios.post(`${API}/hub/addresses`, {
        label: s.label || 'Sans nom',
        address: s.sub || s.label || '',
        coords: [s.lat, s.lon],
        category: cat,
        subcategory: subcat,
      }, { withCredentials: true });
      setItems((cur) => [r.data, ...cur]);
      setSelectedId(r.data.address_id);
      setSearchQ('');
      setCreating(false);
      toast.success('Adresse ajoutée à ton HUB');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="w-full flex h-full" style={{ background: '#0b1220' }} data-testid="hub-addresses-page">
      {/* Left sidebar — category tree + counts */}
      <aside className="w-72 shrink-0 flex flex-col border-r border-white/10" style={{ background: 'rgba(8,12,20,0.85)' }}>
        <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
          <Layers className="w-4 h-4 text-emerald-400" />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-widest text-emerald-300/80">Mon HUB</div>
            <div className="text-sm text-white font-medium">Adresses ({items.length})</div>
          </div>
        </div>

        <div className="p-3">
          <Button size="sm" className="w-full bg-emerald-500 hover:bg-emerald-400 text-black" onClick={() => setCreating(true)} data-testid="hub-addr-add-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> Ajouter une adresse
          </Button>
        </div>

        {/* Phase C — geographic filter chips */}
        {(geoFacets.commune.length > 0 || geoFacets.pays.length > 0) && (
          <div className="px-3 pb-2" data-testid="hub-addr-geo-filters">
            <div className="flex items-center justify-between mb-1">
              <div className="text-[9.5px] uppercase tracking-widest text-gray-500">Filtre géographique</div>
              {geoFilter && (
                <button
                  type="button"
                  onClick={() => setGeoFilter(null)}
                  className="text-[10px] text-emerald-400 hover:text-emerald-300"
                  data-testid="hub-addr-geo-filter-clear"
                >
                  ✕ Effacer
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1">
              {geoFacets.commune.slice(0, 6).map(([name, count]) => {
                const active = geoFilter?.key === 'commune' && geoFilter?.value === name;
                return (
                  <button
                    type="button"
                    key={`c-${name}`}
                    onClick={() => setGeoFilter(active ? null : { key: 'commune', value: name })}
                    className={`px-1.5 py-0.5 rounded-full text-[10px] border transition ${active ? 'bg-emerald-500 text-black border-emerald-400' : 'border-white/10 text-gray-300 hover:border-emerald-400/50'}`}
                    data-testid={`hub-addr-geo-chip-commune-${name}`}
                    title={`${count} adresse${count > 1 ? 's' : ''} à ${name}`}
                  >
                    {name} <span className="text-gray-500">({count})</span>
                  </button>
                );
              })}
              {geoFacets.pays.length > 1 && geoFacets.pays.slice(0, 3).map(([name, count]) => {
                const active = geoFilter?.key === 'pays' && geoFilter?.value === name;
                return (
                  <button
                    type="button"
                    key={`p-${name}`}
                    onClick={() => setGeoFilter(active ? null : { key: 'pays', value: name })}
                    className={`px-1.5 py-0.5 rounded-full text-[10px] border transition ${active ? 'bg-amber-500 text-black border-amber-400' : 'border-white/10 text-gray-300 hover:border-amber-400/50'}`}
                    data-testid={`hub-addr-geo-chip-pays-${name}`}
                    title={`${count} en ${name}`}
                  >
                    🌍 {name} <span className="text-gray-500">({count})</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-2 pb-4" data-testid="hub-addr-tree">
          {['pro', 'perso'].map((cat) => {
            const catItems = tree[cat] || {};
            const total = Object.values(catItems).reduce((n, arr) => n + arr.length, 0);
            const isPro = cat === 'pro';
            const CatIcon = isPro ? Briefcase : Home;
            return (
              <div key={cat} className="mb-1">
                <button
                  type="button"
                  onClick={() => setExpandedCat((c) => ({ ...c, [cat]: !c[cat] }))}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm ${isPro ? 'text-amber-100 hover:bg-amber-500/10' : 'text-blue-100 hover:bg-blue-500/10'}`}
                  data-testid={`hub-addr-cat-${cat}`}
                >
                  {expandedCat[cat] ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                  <CatIcon className={`w-3.5 h-3.5 ${isPro ? 'text-amber-400' : 'text-blue-400'}`} />
                  <span className="flex-1 text-left uppercase tracking-wider text-[11px] font-semibold">{isPro ? 'Pro' : 'Perso'}</span>
                  <span className="text-[11px] text-gray-400">{total}</span>
                </button>
                {expandedCat[cat] && (
                  <div className="ml-4 pl-2 border-l border-white/5">
                    {Object.entries(catItems).sort(([a], [b]) => a.localeCompare(b)).map(([sub, arr]) => {
                      const key = `${cat}/${sub}`;
                      const open = expandedSub[key] !== false;
                      return (
                        <div key={key} className="mt-0.5">
                          <button
                            type="button"
                            onClick={() => setExpandedSub((s) => ({ ...s, [key]: !open }))}
                            className="w-full flex items-center gap-2 px-2 py-1 rounded text-[12px] text-gray-300 hover:bg-white/5"
                            data-testid={`hub-addr-sub-${cat}-${sub}`}
                          >
                            {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                            <span className="flex-1 text-left truncate">{sub}</span>
                            <span className="text-[10px] text-gray-500">{arr.length}</span>
                          </button>
                          {open && arr.map((a) => (
                            <button
                              type="button"
                              key={a.address_id}
                              onClick={() => setSelectedId(a.address_id)}
                              className={`w-full text-left flex items-center gap-2 px-2 py-1 rounded text-[12px] transition ${selectedId === a.address_id ? 'bg-emerald-500/20 text-emerald-100' : 'text-gray-400 hover:bg-white/5 hover:text-gray-100'}`}
                              data-testid={`hub-addr-item-${a.address_id}`}
                            >
                              <MapPin className={`w-3 h-3 shrink-0 ${(a.project_ids || []).length > 0 ? 'text-emerald-400' : ''}`} />
                              <span className="truncate">{a.label}</span>
                            </button>
                          ))}
                        </div>
                      );
                    })}
                    {Object.keys(catItems).length === 0 && (
                      <div className="px-2 py-1 text-[11px] text-gray-500 italic">Aucune adresse</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </aside>

      {/* Center — map + search */}
      <main className="flex-1 relative" data-testid="hub-addr-map-container">
        {/* Search overlay */}
        <div className="absolute top-3 left-3 right-3 z-[500] flex items-center gap-2 pointer-events-none">
          <div className="pointer-events-auto flex-1 max-w-xl relative">
            {creating ? (
              <NewAddressSearch onCreate={doCreateFromSuggestion} onCancel={() => setCreating(false)} />
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <Input
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  placeholder="Filtrer mes adresses… (label, sous-cat, adresse)"
                  className="pl-9 bg-black/60 border-white/10 text-white placeholder:text-gray-500 shadow-lg"
                  data-testid="hub-addr-filter"
                />
                {searchQ && (
                  <button type="button" onClick={() => setSearchQ('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-white">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            )}
          </div>
          <div className="pointer-events-auto flex bg-black/60 rounded-md p-1 shadow-lg border border-white/10">
            {['satellite', 'street'].map((t) => (
              <button
                type="button"
                key={t}
                onClick={() => setTileType(t)}
                className={`px-2 py-1 text-[11px] rounded ${tileType === t ? 'bg-emerald-500 text-black font-medium' : 'text-gray-300 hover:text-white'}`}
                data-testid={`hub-addr-tile-${t}`}
              >
                {t === 'satellite' ? 'Satellite' : 'Plan'}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setHeatmap((v) => !v)}
              className={`px-2 py-1 text-[11px] rounded ${heatmap ? 'bg-emerald-500 text-black font-medium' : 'text-gray-300 hover:text-white'}`}
              data-testid="hub-addr-heatmap-toggle"
              title="Choropleth des départements par nombre d'adresses"
            >
              Heatmap
            </button>
          </div>
        </div>

        <MapContainer center={mapCenter} zoom={items.length > 0 ? 6 : 5} className="w-full h-full" preferCanvas>
          {tileType === 'satellite' ? (
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution="Tiles © Esri"
              maxZoom={19}
            />
          ) : (
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="© OpenStreetMap"
              maxZoom={19}
            />
          )}
          {filteredItems.map((a) => {
            if (!Array.isArray(a.coords) || a.coords.length !== 2) return null;
            return (
              <Marker
                key={a.address_id}
                position={[a.coords[0], a.coords[1]]}
                icon={makePin(a.category, (a.project_ids || []).length > 0, selectedId === a.address_id)}
                eventHandlers={{ click: () => setSelectedId(a.address_id) }}
              />
            );
          })}
          {heatmap && (
            <DepartementHeatmap
              addresses={items}
              activeDepartement={geoFilter?.key === 'departement' ? geoFilter.value : null}
              onDepartementClick={(name) => setGeoFilter({ key: 'departement', value: name })}
            />
          )}
          {selected && <FlyTo coords={selected.coords} />}
        </MapContainer>

        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 z-[400]">
            <Loader2 className="w-6 h-6 text-emerald-400 animate-spin" />
          </div>
        )}
      </main>

      {/* Right panel — detail */}
      {selected && (
        <AddressDetailPanel
          address={selected}
          onClose={() => setSelectedId(null)}
          onPatch={(patch) => doPatch(selected.address_id, patch)}
          onDelete={() => doDelete(selected.address_id)}
          onShare={() => doShare(selected.address_id)}
          onRevokeShare={() => doRevokeShare(selected.address_id)}
          onOpenProject={(pid) => navigate(`/projects/${pid}`)}
          onCreateProject={() => {
            // Send to New project wizard with prefilled address (query params)
            const q = new URLSearchParams({
              from_hub: selected.address_id,
              address: selected.address || '',
              lat: String(selected.coords?.[0] || ''),
              lon: String(selected.coords?.[1] || ''),
            }).toString();
            navigate(`/projects?new=1&${q}`);
          }}
        />
      )}
    </div>
  );
}

/* -------- NewAddressSearch — inline autocomplete to add a new address -------- */

function NewAddressSearch({ onCreate, onCancel }) {
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('pro');
  const { items: suggestions, loading } = useAddressSuggest(q, { minChars: 3, delayMs: 250, limit: 6 });

  return (
    <div className="bg-black/80 border border-emerald-500/40 rounded-md p-3 shadow-2xl" style={{ backdropFilter: 'blur(10px)' }}>
      <div className="flex items-center gap-2 mb-2">
        <Plus className="w-3.5 h-3.5 text-emerald-400" />
        <div className="text-[11px] uppercase tracking-widest text-emerald-300 flex-1">Nouvelle adresse</div>
        <div className="flex gap-1">
          {['pro', 'perso'].map((c) => (
            <button
              type="button"
              key={c}
              onClick={() => setCategory(c)}
              className={`px-2 py-0.5 text-[10px] rounded border ${category === c
                ? (c === 'pro' ? 'bg-amber-500/20 border-amber-400 text-amber-100' : 'bg-blue-500/20 border-blue-400 text-blue-100')
                : 'border-gray-700 text-gray-400'}`}
              data-testid={`hub-addr-new-cat-${c}`}
            >
              {c === 'pro' ? 'Pro' : 'Perso'}
            </button>
          ))}
        </div>
        <button type="button" onClick={onCancel} className="p-1 rounded text-gray-400 hover:text-white" data-testid="hub-addr-new-cancel">
          <X className="w-3 h-3" />
        </button>
      </div>
      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Tape une adresse (Nominatim OSM)…"
        className="h-8 bg-black/60 border-white/10 text-white text-sm"
        autoFocus
        data-testid="hub-addr-new-input"
      />
      {q.trim().length >= 3 && (
        <div className="mt-2 max-h-64 overflow-y-auto">
          {loading && <div className="px-2 py-1 text-[11px] text-gray-400"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Recherche…</div>}
          {!loading && suggestions.length === 0 && <div className="px-2 py-1 text-[11px] text-gray-500">Aucune suggestion</div>}
          {!loading && suggestions.map((s, i) => (
            <button
              type="button"
              key={`${s.kind}-${i}-${s.lat || 'na'}`}
              onClick={() => onCreate(s, category)}
              className="w-full text-left px-2 py-1.5 rounded hover:bg-emerald-500/10 flex items-start gap-2 text-[12px]"
              data-testid={`hub-addr-new-suggest-${i}`}
            >
              <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-400" />
              <div className="min-w-0 flex-1">
                <div className="text-white truncate">{s.label}</div>
                {s.sub && <div className="text-gray-400 truncate text-[11px]">{s.sub}</div>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------- AddressDetailPanel — right panel with actions -------- */

function AddressDetailPanel({ address, onClose, onPatch, onDelete, onShare, onRevokeShare, onOpenProject, onCreateProject }) {
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState(address.label);
  useEffect(() => { setLabelDraft(address.label); setEditingLabel(false); }, [address.address_id, address.label]);

  const isPro = address.category === 'pro';
  const shareUrl = address.share_token ? `${window.location.origin}/hub/shared/${address.share_token}` : null;

  return (
    <aside
      className="w-80 shrink-0 border-l border-white/10 flex flex-col overflow-hidden"
      style={{ background: 'rgba(8,12,20,0.9)' }}
      data-testid="hub-addr-detail-panel"
    >
      <div className={`px-4 py-3 border-b border-white/10 flex items-center gap-2 ${isPro ? 'bg-amber-500/5' : 'bg-blue-500/5'}`}>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isPro ? 'bg-amber-500/20' : 'bg-blue-500/20'}`}>
          {isPro ? <Briefcase className="w-4 h-4 text-amber-400" /> : <Home className="w-4 h-4 text-blue-400" />}
        </div>
        <div className="flex-1 min-w-0">
          {editingLabel ? (
            <div className="flex gap-1">
              <Input
                value={labelDraft}
                onChange={(e) => setLabelDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { onPatch({ label: labelDraft }); setEditingLabel(false); }
                  if (e.key === 'Escape') { setLabelDraft(address.label); setEditingLabel(false); }
                }}
                className="h-7 text-sm"
                autoFocus
                data-testid="hub-addr-label-edit"
              />
              <Button size="sm" onClick={() => { onPatch({ label: labelDraft }); setEditingLabel(false); }}>OK</Button>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <div className="text-sm text-white font-medium truncate">{address.label}</div>
              <button type="button" onClick={() => setEditingLabel(true)} className="p-1 text-gray-400 hover:text-white" data-testid="hub-addr-edit-label">
                <Edit3 className="w-3 h-3" />
              </button>
            </div>
          )}
          <div className="text-[10px] text-gray-500 uppercase tracking-wider">{address.subcategory || '—'}</div>
        </div>
        <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-white" data-testid="hub-addr-detail-close">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 text-sm">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500">Adresse</div>
          <div className="text-gray-200 mt-1">{address.address}</div>
          <div className="text-[11px] text-gray-500 mt-1 font-mono">
            {(address.coords?.[0] || 0).toFixed(5)}, {(address.coords?.[1] || 0).toFixed(5)}
          </div>
          {address.geo_meta && Object.keys(address.geo_meta).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1" data-testid="hub-addr-geo-meta">
              {address.geo_meta.commune && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-emerald-500/15 text-emerald-200 border border-emerald-500/30">
                  {address.geo_meta.commune}
                  {address.geo_meta.code_postal ? ` · ${address.geo_meta.code_postal}` : ''}
                </span>
              )}
              {address.geo_meta.departement && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-blue-500/15 text-blue-200 border border-blue-500/30">
                  {address.geo_meta.departement}
                </span>
              )}
              {address.geo_meta.region && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-purple-500/15 text-purple-200 border border-purple-500/30">
                  {address.geo_meta.region}
                </span>
              )}
              {address.geo_meta.pays && (
                <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-amber-500/15 text-amber-200 border border-amber-500/30">
                  {address.geo_meta.pays}
                </span>
              )}
            </div>
          )}
        </div>

        {address.notes && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500">Notes</div>
            <div className="text-gray-300 text-[13px] mt-1 whitespace-pre-wrap">{address.notes}</div>
          </div>
        )}

        {isPro && (
          <div>
            <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Projets liés</div>
            {(address.project_ids || []).length === 0 ? (
              <div className="text-[12px] text-gray-500 italic">Aucun projet</div>
            ) : (
              <div className="space-y-1">
                {(address.project_ids || []).map((pid) => (
                  <button
                    type="button"
                    key={pid}
                    onClick={() => onOpenProject(pid)}
                    className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded bg-white/5 hover:bg-emerald-500/10 text-gray-200 hover:text-emerald-100 text-[12px]"
                    data-testid={`hub-addr-project-${pid}`}
                  >
                    <ExternalLink className="w-3 h-3" />
                    <span className="font-mono truncate">{pid}</span>
                  </button>
                ))}
              </div>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={onCreateProject}
              className="w-full mt-2 border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
              data-testid="hub-addr-create-project"
            >
              <Plus className="w-3 h-3 mr-1.5" /> Créer un projet ici
            </Button>
          </div>
        )}

        {/* Share */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-gray-500 mb-1">Partage</div>
          {shareUrl ? (
            <div className="space-y-1">
              <div className="flex gap-1">
                <Input value={shareUrl} readOnly className="h-7 text-[11px] font-mono" data-testid="hub-addr-share-url" />
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success('Copié'); }} data-testid="hub-addr-share-copy">
                  <Copy className="w-3 h-3" />
                </Button>
              </div>
              <Button size="sm" variant="ghost" onClick={onRevokeShare} className="w-full text-red-400 hover:text-red-300 h-7" data-testid="hub-addr-share-revoke">
                Révoquer
              </Button>
            </div>
          ) : (
            <Button size="sm" variant="outline" onClick={onShare} className="w-full" data-testid="hub-addr-share-btn">
              <Share2 className="w-3 h-3 mr-1.5" /> Générer un lien de partage
            </Button>
          )}
        </div>
      </div>

      <div className="border-t border-white/10 p-3">
        <Button size="sm" variant="ghost" onClick={onDelete} className="w-full text-red-400 hover:bg-red-500/10 hover:text-red-300" data-testid="hub-addr-delete">
          <Trash2 className="w-3 h-3 mr-1.5" /> Supprimer cette adresse
        </Button>
      </div>
    </aside>
  );
}
